import type { ApolloError } from '@apollo/client'
import numeral from 'numeral'

import { notify } from 'components/uiKit/Notification'
import { NotificationType } from 'components/uiKit/Notification/types'
import type { CompanySchemaFragment } from 'gql/__generated__/graphql'
import { t } from 'services/Translation'

import { getResourcesLabel } from './resources'

/**
 * Получает переведенное сообщение об ошибке на основе кода ошибки с бэкенда.
 * Использует паттерн `be.error.${errorCode}` для поиска перевода.
 * Если перевод не найден, возвращает общее сообщение об ошибке.
 *
 * @param errorCode - Код ошибки с бэкенда (например, "LMS_FAIL_PUBLISH", "INVALID_CREDENTIALS")
 * @returns Переведенное сообщение об ошибке или общее сообщение
 *
 * @example
 * getBackendErrorMessage('LMS_FAIL_PUBLISH')
 * // => "Course publishing failed. The connection with LMS might be invalid..."
 *
 * @example
 * getBackendErrorMessage('UNKNOWN_ERROR')
 * // => logs warning and returns general error message
 */
export const getBackendErrorMessage = (errorCode: string | null | undefined): string => {
  if (!errorCode) {
    return ''
  }

  const translationKey = `be.error.${errorCode}`
  const message = t(translationKey)

  if (message === translationKey) {
    // Log warning in dev but don't crash the app
    console.warn(`[Translation] Missing backend error translation for "${translationKey}"`)

    // Return general error message as fallback
    return t('be.error.INTERNAL_SERVER_ERROR')
  }

  return message
}

export const employeeInviteError = (err: ApolloError, company: CompanySchemaFragment) => {
  const limitError = err.graphQLErrors.find((e) => e.message === 'EMPLOYEES_LIMIT_REACHED')

  const diff = Number(company.availableEmployees) - company.employeeCountNonLeenda
  const diffMod = diff < 0 ? 0 : diff

  if (limitError) {
    notify({
      type: NotificationType.error,
      message: t('notify.EMPLOYEES_LIMIT_REACHED', {
        current: company.employeeCountNonLeenda,
        total: company.availableEmployees,
        count: diffMod,
      }),
      duration: 8000,
    })
  }
}

export const storageLimitReached = (err: ApolloError, company: CompanySchemaFragment) => {
  const storageLimitError = err.graphQLErrors.find((e) => e.message === 'STORAGE_LIMIT_REACHED')
  if (!storageLimitError) {
    return
  }

  const diff = Number(company.availableStorage) - Number(company.overallResources.total)
  const diffMod = diff < 0 ? 0 : diff
  notify({
    type: NotificationType.error,
    message: t('notify.STORAGE_LIMIT_REACHED', {
      resources: getResourcesLabel(company.overallResources.total, company.availableStorage),
      diff: numeral(diffMod).format('0.0 b'),
    }),
    duration: 8000,
  })
}
